import React, { forwardRef, useState } from 'react';
import {
	Typography,
	Grid,
	FormControl,
	TextField,
	RadioGroup,
	FormControlLabel,
	Radio,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';

import { BookingBuffer } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';

interface OwnProps {
	value?: BookingBuffer | undefined;
	onChange?: (value: BookingBuffer) => void;
	disabled?: boolean;
	isDelivery?: false | boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const emptyBuffer: BookingBuffer = {
	daysBefore: 0,
	minutesBefore: 0,
	latestTime: 6,
};

const BookingBufferSelector = forwardRef((props: Props, ref) => {
	const { classes, value = emptyBuffer, onChange, isDelivery } = props;
	const { t } = useTranslation();

	const [hasCustomDuration, setHasCustomDuration] = useState(value.daysBefore > 3);
	const bufferType = value.daysBefore === 0 ? 'minutes' : 'hour';

	const renderMinutesBeforeDropdown = () => {
		return (
			<TextField
				select
				variant="standard"
				className={classes.narrowDropdown}
				value={value.minutesBefore}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					onChange?.({
						...value,
						minutesBefore: parseInt(e.target.value, 10),
					});
				}}
				InputLabelProps={{
					shrink: true,
					style: {
						maxWidth: 120,
					},
				}}
				SelectProps={{
					displayEmpty: false,
					native: true,
					MenuProps: {
						className: classes.menu,
					},
				}}
				margin="normal"
				type="number"
				disabled={props.disabled || bufferType !== 'minutes'}
			>
				<option value={0}>{'0 min'}</option>
				<option value={30}>{'30 min'}</option>
				<option value={60}>{'1 h'}</option>
				<option value={90}>{'1 h 30 min'}</option>
				<option value={120}>{'2 h'}</option>
				<option value={180}>{'3 h'}</option>
				<option value={240}>{'4 h'}</option>
				<option value={300}>{'5 h'}</option>
				<option value={360}>{'6 h'}</option>
				<option value={420}>{'7 h'}</option>
				<option value={480}>{'8 h'}</option>
			</TextField>
		);
	};

	const renderByHourDropdown = () => {
		return (
			<TextField
				select
				variant="standard"
				className={classes.narrowDropdown}
				value={value.latestTime}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					onChange?.({
						...value,
						latestTime: parseInt(e.target.value, 10),
					});
				}}
				InputLabelProps={{
					shrink: true,
					style: {
						maxWidth: 120,
					},
				}}
				SelectProps={{
					displayEmpty: false,
					native: true,
					MenuProps: {
						className: classes.menu,
					},
				}}
				margin="normal"
				type="number"
				disabled={props.disabled || bufferType !== 'hour'}
			>
				<option value={6}>{'06:00'}</option>
				<option value={7}>{'07:00'}</option>
				<option value={8}>{'08:00'}</option>
				<option value={9}>{'09:00'}</option>
				<option value={10}>{'10:00'}</option>
				<option value={11}>{'11:00'}</option>
				<option value={12}>{'12:00'}</option>
				<option value={13}>{'13:00'}</option>
				<option value={14}>{'14:00'}</option>
				<option value={15}>{'15:00'}</option>
				<option value={16}>{'16:00'}</option>
				<option value={17}>{'17:00'}</option>
				<option value={18}>{'18:00'}</option>
				<option value={19}>{'19:00'}</option>
				<option value={20}>{'20:00'}</option>
				<option value={21}>{'21:00'}</option>
				<option value={22}>{'22:00'}</option>
				<option value={23}>{'23:00'}</option>
				<option value={24}>{'24:00'}</option>
			</TextField>
		);
	};

	return (
		<Grid container spacing={3}>
			<Grid item>
				<FormControl component="fieldset" className={classes.formControl}>
					<div className={classes.group}>
						<TextField
							label=""
							variant="standard"
							select
							className={classes.textField}
							value={value.daysBefore > 3 ? 4 : value.daysBefore}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const daysBefore = parseInt(e.target.value, 10);
								setHasCustomDuration(daysBefore > 3);
								onChange?.({
									...value,
									daysBefore,
								});
							}}
							InputLabelProps={{
								shrink: true,
							}}
							SelectProps={{
								displayEmpty: false,
								native: true,
								MenuProps: {
									className: classes.menu,
								},
							}}
							margin="normal"
							type="text"
							disabled={props.disabled}
						>
							<option value={0}>{t('category.sameDay', 'Same day')}</option>
							<option value={1}>{t('category.oneDay', '1 day before')}</option>
							<option value={2}>{t('category.twoDays', '2 days before')}</option>
							<option value={3}>{t('category.threeDays', '3 days before')}</option>
							<option value={4}>{t('category.anotherDuration', 'Add another duration')}</option>
						</TextField>
						{hasCustomDuration && (
							<Grid container alignItems="flex-end">
								<Grid item>
									<TextField
										label=""
										type="number"
										variant="standard"
										margin="normal"
										className={classes.superNarrowDropdown}
										value={value.daysBefore}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											onChange?.({
												...value,
												daysBefore: Math.max(parseInt(e.target.value, 10), 0),
											});
										}}
										InputLabelProps={{
											shrink: true,
										}}
										InputProps={{
											classes: {
												input: classes.textFieldStyle,
											},
										}}
										disabled={props.disabled}
									/>
								</Grid>
								<Grid item>
									<Typography className={classes.smallBotMargin}>
										{t('category.daysBefore', 'days before')}
									</Typography>
								</Grid>
							</Grid>
						)}
					</div>
				</FormControl>
			</Grid>
			<Grid item>
				<FormControl component="fieldset" className={classes.formControl}>
					<RadioGroup
						aria-label="Duration"
						name="bookingBuffer"
						className={classes.group}
						value={bufferType}
					>
						<FormControlLabel
							value="minutes"
							control={<Radio color="primary" />}
							classes={{
								label: classes.fullWidth,
							}}
							disabled={props.disabled || bufferType !== 'minutes'}
							label={
								<Grid container alignItems="flex-end">
									<Grid item>{renderMinutesBeforeDropdown()}</Grid>
									<Grid item>
										<Typography className={classes.smallBotMargin}>
											{isDelivery
												? t('category.beforeDelivery')
												: t('category.beforeBookingBegins')}
										</Typography>
									</Grid>
								</Grid>
							}
						/>
						<FormControlLabel
							value="hour"
							control={<Radio color="primary" />}
							classes={{
								label: classes.fullWidth,
							}}
							disabled={props.disabled || bufferType !== 'hour'}
							label={
								<Grid container alignItems="flex-end">
									<Grid item>
										<Typography className={classes.smallBotMargin}>
											{t('category.byHour', 'by')}
										</Typography>
									</Grid>
									<Grid item>{renderByHourDropdown()}</Grid>
								</Grid>
							}
						/>
					</RadioGroup>
				</FormControl>
			</Grid>
		</Grid>
	);
});

const styles = (theme: Theme) =>
	createStyles({
		textField: {
			maxWidth: 380,
		},
		menu: {
			width: 200,
		},
		subHeading: {
			marginTop: theme.spacing(2),
		},
		formControl: {
			width: '100%',
		},
		group: {
			margin: theme.spacing(1, 0),
		},
		fullWidth: {
			width: '100%',
		},
		narrowDropdown: {
			maxWidth: 150,
			marginLeft: theme.spacing(1),
			marginRight: theme.spacing(1),
		},
		smallBotMargin: {
			marginBottom: '0.75em',
		},
		textFieldStyle: {
			WebkitAppearance: 'textfield',
			margin: 0,
		},
		superNarrowDropdown: {
			maxWidth: 50,
			marginLeft: theme.spacing(1),
			marginRight: theme.spacing(1),
		},
	});

export default withStyles(styles)(BookingBufferSelector);
